<script lang="ts" setup>
import icCircleGreen from '@/assets/img/icon/check-sm.svg';
import icCircleIcon from '@/assets/img/icon/uncheck-sm.svg';
import AppTypography from './AppTypography.vue';

const { password = '' } = defineProps<{
  password?: string;
}>();

const appConfig = useRuntimeConfig();

const hasEightCharacters = computed(() => password.length >= Number(appConfig.app.passwordCriteria.minLength));
const hasOneUppercase = computed(() => {
  const uppercaseMatches = password.match(/[A-Z]/g);
  return uppercaseMatches && uppercaseMatches.length >= Number(appConfig.app.passwordCriteria.minUppercase);
});
const hasSpecialCharacter = computed(() => {
  const specialCharMatches = password.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/g);
  return specialCharMatches && specialCharMatches.length >= Number(appConfig.app.passwordCriteria.minSpecialCharacters);
});
const hasNumber = computed(() => /\d/.test(password));
</script>
<template>
  <v-sheet
    v-if="password"
    class="password-criteria d-flex flex-column"
    :class="{
      'password-criteria--eight-chars': hasEightCharacters,
      'password-criteria--one-uppercase': hasOneUppercase,
      'password-criteria--one-number': hasNumber,
    }"
  >
    <AppTypography type="caption-1" class="font-700 text-uppercase mb-2"> Password must contain: </AppTypography>

    <div
      class="d-flex pa-o"
      :class="{
        'password-checked': hasEightCharacters,
      }"
      data-testid="eigth-characters"
    >
      <img :src="hasEightCharacters ? icCircleGreen : icCircleIcon" width="28px" height="28px" />
      <AppTypography type="footnote-2" class="ml-1 pa-1" :class="{ 'text-check': hasEightCharacters }">
        At least {{ appConfig.app.passwordCriteria.minLength }} characters
      </AppTypography>
    </div>
    <div
      class="d-flex pa-o mt-1"
      :class="{
        'password-checked': hasOneUppercase,
      }"
      data-testid="one-uppercase"
    >
      <img :src="hasOneUppercase ? icCircleGreen : icCircleIcon" width="28px" height="28px" />
      <AppTypography type="footnote-2" class="ml-1 pa-1" :class="{ 'text-check': hasOneUppercase }">
        Must include both uppercase and lowercase letters.
      </AppTypography>
    </div>
    <div
      class="d-flex pa-o mt-1"
      :class="{
        'password-checked': hasSpecialCharacter,
      }"
      data-testid="one-special-character"
    >
      <img :src="hasSpecialCharacter ? icCircleGreen : icCircleIcon" width="28px" height="28px" />
      <AppTypography type="footnote-2" class="ml-1 pa-1" :class="{ 'text-check': hasSpecialCharacter }">
        Must contain at least one special character (e.g., !@#$%^&*)
      </AppTypography>
    </div>
    <div
      class="d-flex pa-o mt-1"
      :class="{
        'password-checked': hasNumber,
      }"
      data-testid="one-number"
    >
      <img :src="hasNumber ? icCircleGreen : icCircleIcon" width="28px" height="28px" />
      <AppTypography type="footnote-2" class="ml-1 pa-1" :class="{ 'text-check': hasNumber }">
        Must contain at least one numeric character (0-9).
      </AppTypography>
    </div>
  </v-sheet>
</template>
<style lang="scss" scoped>
.password-criteria {
  background-color: transparent;
  .password-checked {
    color: #05704e;
  }
  .text-check {
    color: #b2c582;
  }
}
</style>
